import { runInAction, action, computed, makeAutoObservable } from 'mobx';
import {
  Alert as AlertResource,
  AlertPriority,
  AlertStatus,
  AlarmEventType,
  AlertOverviewEntry,
  SummaryProfile,
  getDate,
  Location,
  AlertV2Entry,
  AlertAction,
  DeviceEntryProps,
  AlertUpdateEvent,
  HcpGroupHistory,
  ProximityResolveAction,
} from '@ixicare/resources';
import { State } from '../types';
import { backoff } from '~/utils/promises';

class Alert {
  rootStore: State;

  uuid: string;
  status: AlertStatus;
  priority: AlertPriority;

  // v2 types
  eventType: AlarmEventType;
  alertUpdateEvents: AlertUpdateEvent[] = [];
  resident: null | SummaryProfile = null;
  device:
    | null
    | DeviceEntryProps
    | {
        uuid: string;
        id: number;
        description?: string;
        batteryLevel?: number;
        phoneNumber?: string;
        locationIsStatic?: boolean;
        location?: Location;
      } = null;
  acceptedBy: null | SummaryProfile = null;
  resolvedBy: null | SummaryProfile = null;
  resolvedTime: null | Date = null;
  acceptedTime: null | Date = null;
  cancelledTime: null | Date = null;
  resolvedReason: null | string = null;
  resolvedProximityAction: null | string = null;
  created: Date;
  alarmDescription: string | null;
  locationDescription: string | null;
  hcpGroupHistory: HcpGroupHistory[] = [];

  // Custom types
  isAcceptedByUser: boolean;
  isAllowedToResolve = false;
  isRejected = false;

  setDetailData(alert: AlertV2Entry) {
    this.uuid = alert.uuid;
    this.setStatus(alert.status);
    this.priority = alert.priority;
    this.alertUpdateEvents = alert.alertUpdateEvents;

    const acceptUpdateEvent = alert.alertUpdateEvents.filter((e) => e.action === AlertAction.Accept)[0];
    const resolveUpdateEvent = alert.alertUpdateEvents
      .filter((e) => e.action === AlertAction.Resolve)
      .sort((a, b) => a.created.getTime() - b.created.getTime())[0];
    const createAlarmEvent =
      alert.earliest_alarm_event ??
      alert.alarmEvents.filter((e) => e.eventType !== AlarmEventType.DeviceCancellation)[0];
    const cancelAlarmEvent = alert.alarmEvents.filter((e) => e.eventType === AlarmEventType.DeviceCancellation)[0];

    if (!this.isAcceptedByUser) {
      this.isAcceptedByUser = acceptUpdateEvent?.profile?.uuid === this.rootStore.auth.user?.uuid;
    }

    this.eventType = alert.most_urgent_alarm_event?.eventType ?? createAlarmEvent.eventType;
    this.device = alert.device;
    this.resident = alert.deviceOwner;
    this.acceptedBy = acceptUpdateEvent?.profile ?? null;
    this.resolvedBy = resolveUpdateEvent?.profile ?? null;
    this.resolvedTime = resolveUpdateEvent?.created ?? null;
    this.resolvedProximityAction = resolveUpdateEvent?.proximityResolveAction ?? null;

    this.acceptedTime = acceptUpdateEvent?.created ?? null;
    this.cancelledTime = cancelAlarmEvent?.created ?? null;
    this.created = createAlarmEvent.originalTimestamp ?? createAlarmEvent.created;
    this.resolvedReason = resolveUpdateEvent?.reason ?? null;
    this.alarmDescription = alert.alarmDescription;
    this.locationDescription = alert.locationDescription;
    this.hcpGroupHistory = alert.hcpGroupHistory;
  }

  setOverviewData(alert: AlertOverviewEntry) {
    this.uuid = alert.uuid;
    this.setStatus(alert.status);
    this.priority = alert.priority;
    this.eventType = alert.eventType;
    this.resident = alert.resident ?? null;
    this.device = alert.device ?? null;
    this.acceptedBy = alert.acceptedBy ?? null;
    this.resolvedBy = alert.resolvedBy ?? null;
    this.resolvedTime = alert.resolvedTime ? getDate(alert.resolvedTime) : null;
    this.acceptedTime = alert.acceptedTime ? getDate(alert.acceptedTime) : null;
    this.cancelledTime = alert.cancelledTime ? getDate(alert.cancelledTime) : null;
    this.resolvedReason = alert.resolvedReason ?? null;
    this.created = getDate(alert.created);
    this.alarmDescription = alert.alarmDescription ?? null;
    this.locationDescription = alert.locationDescription ?? null;

    if (!this.isAcceptedByUser) {
      this.isAcceptedByUser = alert.acceptedBy?.uuid === this.rootStore.auth.user?.uuid;
    }
  }

  setStatus(status: AlertStatus) {
    if (this.status === AlertStatus.Resolved || this.status === AlertStatus.Cancelled) return;
    if (status === AlertStatus.Open && this.status) return;
    this.status = status;
  }

  constructor(rootStore: State, alertDetail?: AlertV2Entry, alertOverview?: AlertOverviewEntry) {
    this.rootStore = rootStore;
    if (alertDetail) {
      this.setDetailData(alertDetail);
    }
    if (alertOverview) {
      this.setOverviewData(alertOverview);
    }
    makeAutoObservable(this, {
      accept: action.bound,
      resolve: action.bound,
      isAccepted: computed,
      setDetailData: action.bound,
      setOverviewData: action.bound,
      setStatus: action.bound,
      isOpen: computed,
      isResolved: computed,
      isCancelled: computed,
      updateAction: action.bound,
      isHandledByOther: computed,
      allowUserToResolve: action.bound,
    });
  }

  get description() {
    if (this.alarmDescription) {
      return this.alarmDescription;
    }
    return this.resident
      ? `${this.resident?.lastName.toUpperCase()} ${this.resident?.firstName}`
      : (this.device?.description ?? '-');
  }

  get batteryLevel() {
    return this.device?.batteryLevel;
  }

  get hasFallEvent() {
    return this.eventType === AlarmEventType.FallDetection;
  }

  get isOpen() {
    return this.status === AlertStatus.Open || (this.status === AlertStatus.Accepted && this.isAcceptedByUser);
  }

  get isResolved() {
    return [AlertStatus.Resolved, AlertStatus.Cancelled].includes(this.status);
  }

  get isAccepted() {
    return this.status === AlertStatus.Accepted;
  }

  get isAcceptedOwnExcluded() {
    return this.status === AlertStatus.Accepted && !this.isAcceptedByUser;
  }

  get isHandledByOther() {
    return [AlertStatus.Accepted, AlertStatus.Resolved].includes(this.status) && !this.isAcceptedByUser;
  }

  get isCancelled() {
    return this.status === AlertStatus.Cancelled;
  }

  get cancelled() {
    return this.status === AlertStatus.Cancelled;
  }

  get endTime() {
    return this.acceptedTime ?? this.resolvedTime ?? this.cancelledTime;
  }

  get answeredBy() {
    return this.acceptedBy ?? this.resolvedBy;
  }

  updateAction(
    type: 'cancel' | 'accept' | 'resolve' | 'reject',
    status: AlertStatus,
    reason?: string,
    key?: 'acceptAction' | 'resolveAction' | 'cancelAction'
  ) {
    const oldStatus = this.status;
    this.status = status;

    if (type === 'accept') {
      this.isAcceptedByUser = true;
    }

    return new Promise((resolve) => {
      const body = reason ? { reason } : undefined;
      const resolveBody =
        type === 'resolve' ? { ...body, proximityResolveAction: ProximityResolveAction.DASHBOARD } : body;
      backoff(() => AlertResource.updateAlertAction(this.uuid, type, resolveBody), {
        maxRetries: 5,
      })
        .then((data) => {
          runInAction(() => {
            if (key) {
              this[key] = data;
            }
            this.status = status;
          });
          resolve(undefined);
        })
        .catch((e) => {
          console.error('AlertResource.updateAlertAction', e);
          this.status = oldStatus;
          this.isAcceptedByUser = false;
          resolve(undefined);
        });
    });
  }

  reject(reason: string) {
    this.isRejected = true;
    this.rootStore.alerts.rejectAlerts([this]);
    return this.updateAction('reject', AlertStatus.Open, reason);
  }

  accept() {
    this.rootStore.alerts.rejectAlerts([this]);
    return this.updateAction('accept', AlertStatus.Accepted, undefined, 'acceptAction');
  }

  resolve(reason: string) {
    this.rootStore.alerts.rejectAlerts([this]);
    return this.updateAction('resolve', AlertStatus.Resolved, reason, 'resolveAction');
  }

  allowUserToResolve() {
    this.isAllowedToResolve = true;
  }
}

export default Alert;
